/**
 * HR Commission Runs — Phase 1C sub-pipeline UI.
 *
 *   entry → calculated → reviewed → locked → (feeds payroll)
 *
 * Locking writes one adjustment_add data_entry per emp into the
 * specified payroll period, so the next payroll Run Draft picks the
 * commission amount up automatically.
 */
import { useState } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import {
  useCommissionRuns,
  useCreateCommissionRun,
  useCommissionPerformance,
  useUpsertCommissionPerformance,
  useCalculateCommission,
  useReviewCommission,
  useLockCommission,
  useEmployees,
  type CommissionRunRow,
} from '../../hooks';
import { Plus, Play, CheckSquare, Lock } from 'lucide-react';
import { Modal } from '../../components/ui/Modal';
import { useToast } from '../../components/ui/toast';

function statusBadge(s: CommissionRunRow['status']): string {
  const m: Record<string, string> = {
    entry: 'bg-amber-100 text-amber-700',
    calculated: 'bg-blue-100 text-blue-700',
    reviewed: 'bg-purple-100 text-purple-700',
    locked: 'bg-green-100 text-green-700',
    voided: 'bg-gray-100 text-gray-500',
  };
  return m[s] ?? 'bg-gray-100';
}

/** Content-only version used by the Commission hub. */
export function HRCommissionRunsContent() {
  const runs = useCommissionRuns();
  const createRun = useCreateCommissionRun();
  const employees = useEmployees() as { data: Array<{ emp_code: string; first_name: string; last_name: string; nickname?: string | null }> | undefined };

  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const performance = useCommissionPerformance(selectedRunId);
  const upsertPerf = useUpsertCommissionPerformance(selectedRunId ?? '');
  const calculate = useCalculateCommission(selectedRunId ?? '');
  const review = useReviewCommission(selectedRunId ?? '');
  const lock = useLockCommission(selectedRunId ?? '');

  const selectedRun = (runs.data ?? []).find((r) => r.id === selectedRunId) ?? null;
  const toast = useToast();
  const [createOpen, setCreateOpen] = useState(false);
  const [cStart, setCStart] = useState('');
  const [cEnd, setCEnd] = useState('');
  const [lockOpen, setLockOpen] = useState(false);
  const [lockPeriod, setLockPeriod] = useState(new Date().toISOString().slice(0, 7));

  function handleCreate() {
    setCStart('');
    setCEnd('');
    setCreateOpen(true);
  }
  async function submitCreate() {
    if (!cStart || !cEnd) { toast.error('กรอกวันเริ่ม–สิ้นสุดงวด'); return; }
    try {
      const r = await createRun.mutateAsync({ period_start: cStart, period_end: cEnd });
      setSelectedRunId(r.id);
      setCreateOpen(false);
    } catch (e) { toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด'); }
  }

  function handleLock() {
    if (!selectedRunId) return;
    setLockPeriod(new Date().toISOString().slice(0, 7));
    setLockOpen(true);
  }
  async function submitLock() {
    if (!lockPeriod) { toast.error('ระบุงวดเงินเดือน (YYYY-MM)'); return; }
    try {
      const r = await lock.mutateAsync({ feeds_payroll_period: lockPeriod });
      setLockOpen(false);
      toast.success(`ล็อกแล้ว — ส่ง ${r.fed_entries} รายการเข้าเงินเดือน ${lockPeriod}`);
    } catch (e) { toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด'); }
  }

  return (
    <>
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-gray-600">กรอกผลงาน → คำนวณ → ตรวจ → ล็อก → เข้าเงินเดือน</p>
        <button onClick={handleCreate} className="btn-primary text-sm">
          <Plus className="w-4 h-4" /> สร้าง Run ใหม่
        </button>
      </div>
      {/* Run list */}
      {!selectedRunId && (
        <div className="space-y-2">
          {(runs.data ?? []).map((r) => (
            <button
              key={r.id}
              onClick={() => setSelectedRunId(r.id)}
              className="card p-3 w-full text-left hover:bg-gray-50 flex items-center justify-between"
            >
              <div>
                <div className="font-medium">{r.period_start} → {r.period_end}</div>
                {r.feeds_payroll_period && (
                  <div className="text-xs text-gray-500">feeds {r.feeds_payroll_period}</div>
                )}
              </div>
              <span className={`badge ${statusBadge(r.status)}`}>{r.status}</span>
            </button>
          ))}
          {(runs.data ?? []).length === 0 && (
            <p className="text-center text-gray-500 py-8">ยังไม่มี run</p>
          )}
        </div>
      )}

      {/* Run detail */}
      {selectedRunId && selectedRun && (
        <div>
          <button onClick={() => setSelectedRunId(null)} className="text-sm text-brand-600 mb-3">← กลับ</button>
          <div className="flex items-center gap-2 mb-3">
            <h2 className="font-semibold">{selectedRun.period_start} → {selectedRun.period_end}</h2>
            <span className={`badge ${statusBadge(selectedRun.status)}`}>{selectedRun.status}</span>
          </div>

          {/* Performance table */}
          <div className="card overflow-hidden mb-3">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-2 py-1">พนักงาน</th>
                  <th className="text-right px-2 py-1">ยอดขาย</th>
                  <th className="text-right px-2 py-1">หน่วย</th>
                  <th className="text-right px-2 py-1">กำไร</th>
                  <th className="text-right px-2 py-1">คอม</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(employees.data ?? []).map((emp) => {
                  const row = performance.data?.find((p) => p.emp_code === emp.emp_code);
                  return (
                    <PerfRow
                      key={emp.emp_code}
                      empCode={emp.emp_code}
                      empName={`${emp.first_name} ${emp.last_name}${emp.nickname ? ` (${emp.nickname})` : ''}`}
                      row={row ?? null}
                      onSave={(b) => upsertPerf.mutateAsync(b)}
                      disabled={selectedRun.status === 'locked' || selectedRun.status === 'voided'}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Stage actions */}
          <div className="space-y-2">
            {selectedRun.status === 'entry' && (
              <button onClick={() => calculate.mutateAsync().catch((e) => toast.error(e.message))} className="btn-cta w-full">
                <Play className="w-4 h-4" /> คำนวณคอมมิชชั่น
              </button>
            )}
            {selectedRun.status === 'calculated' && (
              <button onClick={() => review.mutateAsync().catch((e) => toast.error(e.message))} className="btn-cta w-full">
                <CheckSquare className="w-4 h-4" /> ทำเครื่องหมายว่าตรวจแล้ว
              </button>
            )}
            {selectedRun.status === 'reviewed' && (
              <button onClick={handleLock} className="btn-primary w-full">
                <Lock className="w-4 h-4" /> ล็อก + ส่งเข้าเงินเดือน
              </button>
            )}
            {selectedRun.status === 'locked' && selectedRun.feeds_payroll_period && (
              <div className="text-sm text-center text-green-700 py-2">
                ✓ ส่งเข้างวดเงินเดือน {selectedRun.feeds_payroll_period} แล้ว
              </div>
            )}
          </div>
        </div>
      )}

      <Modal open={createOpen} onClose={() => setCreateOpen(false)} title="สร้างงวดคอมมิชชั่นใหม่">
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label">วันเริ่มงวด</label><input type="date" className="input" value={cStart} onChange={(e) => setCStart(e.target.value)} /></div>
            <div><label className="label">วันสิ้นสุดงวด</label><input type="date" className="input" value={cEnd} onChange={(e) => setCEnd(e.target.value)} /></div>
          </div>
          <div className="flex gap-2">
            <button className="btn-secondary flex-1" onClick={() => setCreateOpen(false)}>ยกเลิก</button>
            <button className="btn-primary flex-1" onClick={submitCreate} disabled={createRun.isPending}>สร้าง</button>
          </div>
        </div>
      </Modal>

      <Modal open={lockOpen} onClose={() => setLockOpen(false)} title="ล็อก + ส่งเข้าเงินเดือน">
        <div className="space-y-3">
          <div><label className="label">งวดเงินเดือนปลายทาง (YYYY-MM)</label><input className="input" value={lockPeriod} onChange={(e) => setLockPeriod(e.target.value)} placeholder="2026-05" /></div>
          <div className="flex gap-2">
            <button className="btn-secondary flex-1" onClick={() => setLockOpen(false)}>ยกเลิก</button>
            <button className="btn-primary flex-1" onClick={submitLock} disabled={lock.isPending}>ล็อก</button>
          </div>
        </div>
      </Modal>
    </>
  );
}

/** Standalone page kept for legacy /hr/commission-runs (now redirects). */
export function HRCommissionRuns() {
  return (
    <AdminLayout title="คำนวณคอมมิชชั่น">
      <HRCommissionRunsContent />
    </AdminLayout>
  );
}

function PerfRow({
  empCode,
  empName,
  row,
  onSave,
  disabled,
}: {
  empCode: string;
  empName: string;
  row: import('../../hooks').CommissionPerformanceRow | null;
  onSave: (b: { emp_code: string; gross_sales: number; units_sold: number; gross_profit: number }) => Promise<unknown>;
  disabled: boolean;
}) {
  const [grossSales, setGrossSales] = useState(row?.gross_sales ?? 0);
  const [unitsSold, setUnitsSold] = useState(row?.units_sold ?? 0);
  const [grossProfit, setGrossProfit] = useState(row?.gross_profit ?? 0);
  const toast = useToast();

  async function commit() {
    if (disabled) return;
    try {
      await onSave({ emp_code: empCode, gross_sales: grossSales, units_sold: unitsSold, gross_profit: grossProfit });
    } catch (e) {
      toast.error(`บันทึกไม่สำเร็จ: ${e instanceof Error ? e.message : 'เกิดข้อผิดพลาด'}`);
    }
  }

  return (
    <tr>
      <td className="px-2 py-1 text-xs">
        <div>{empName}</div>
        <div className="text-gray-400 font-mono">{empCode}</div>
      </td>
      <td className="px-1 py-1">
        <input
          type="number"
          className="input text-right text-xs py-0.5 px-1"
          value={grossSales}
          onChange={(e) => setGrossSales(Number(e.target.value))}
          onBlur={commit}
          disabled={disabled}
        />
      </td>
      <td className="px-1 py-1">
        <input
          type="number"
          className="input text-right text-xs py-0.5 px-1"
          value={unitsSold}
          onChange={(e) => setUnitsSold(Number(e.target.value))}
          onBlur={commit}
          disabled={disabled}
        />
      </td>
      <td className="px-1 py-1">
        <input
          type="number"
          className="input text-right text-xs py-0.5 px-1"
          value={grossProfit}
          onChange={(e) => setGrossProfit(Number(e.target.value))}
          onBlur={commit}
          disabled={disabled}
        />
      </td>
      <td className="px-2 py-1 text-right text-xs font-semibold">
        {row?.calculated_amount != null
          ? row.calculated_amount.toLocaleString('th-TH')
          : <span className="text-gray-300">—</span>}
      </td>
    </tr>
  );
}
